import { Component, Input, OnInit } from '@angular/core';
import { HttpClient, HttpEvent, HttpEventType, HttpHeaders, HttpRequest } from '@angular/common/http';

import { CustomFormComponent } from '@services/formBuilder/abstract-custom-form-field';
import {
  FormFieldUploaderValue,
  FormFileUploaderConfig,
  FormFileUploaderMetadata,
  UploadFile,
  UploadFileStatus
} from './model';
import {
  CommsService,
  ObjectFileType,
  ObjectItem,
  ObjectsService,
  SubscriberService,
  UserdataService,
  ViewerService
} from '@services';

import { of } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import { awaitHandler } from '@utils/awaitHandler';
import { TranslateService } from '@ngx-translate/core';
import { catchError, filter, switchMap, tap } from 'rxjs/operators';
import {
  assign,
  each,
  filter as _filter,
  find,
  map,
  some,
  split,
  toLower,
  trim,
  startsWith,
  endsWith,
  flatten,
  reject,
  includes
} from 'lodash';
import { NGXLogger } from 'ngx-logger';

@Component({
  selector: 'app-form-file-uploader',
  templateUrl: './form-file-uploader.component.html',
  styleUrls: ['./form-file-uploader.component.scss'],
})
export class FormFileUploaderComponent extends CustomFormComponent implements OnInit {
  @Input() config: FormFileUploaderConfig;
  @Input() value: number;
  public UploadFileStatus = UploadFileStatus;
  public files: UploadFile[] = [];
  public isDragging = false;
  private markedForRemovalIds: number[] = [];
  private tempUploadedObjects: ObjectItem[];

  constructor(
    private http: HttpClient,
    private commsService: CommsService,
    private objectsService: ObjectsService,
    private translation: TranslateService,
    private userDataService: UserdataService,
    private subscriberService: SubscriberService,
    private viewerService: ViewerService,
    private logger: NGXLogger
  ) {
    super();
  }

  ngOnInit() {
    this.defineConfig();
    this.defineFiles();
  }

  public removeFile(index: number) {
    const file = this.files?.[index];

    if (this.config.deferredUpload && this.config.requestRemovingOnAction && file?.status !== UploadFileStatus.Error) {
      this.markedForRemovalIds.push(file.object.objectID);
    }

    this.files.splice(index, 1);
    this.emitValue();
  }

  public onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      this.addFiles(Array.from(input.files));
    }
  }

  public onDragOver(event: DragEvent) {
    event.preventDefault();
    this.isDragging = true;
  }

  public onDragLeave(event: DragEvent) {
    event.preventDefault();
    this.isDragging = false;
  }

  public onDrop(event: DragEvent) {
    event.preventDefault();
    this.isDragging = false;
    const transferFiles = Array.from(event?.dataTransfer?.files);
    const files = _filter(transferFiles, (file) => this.matchesAccept(file, this.config.accept));

    if (files.length <= this.config.limit) {
      this.addFiles(files);
    }
  }

  public addFiles(fileList: File[]) {
    each(fileList, (file) => {
      const fileObj: UploadFile = {
        data: file,
        progress: null,
        preview: null,
        object: {
          objectUUID: uuidv4(),
        } as ObjectItem,
        icon: this.objectsService.getIconByType(file.type)
      };

      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = () => {
          fileObj.preview = reader.result as string;
        };
        reader.readAsDataURL(file);
      }

      if (!this.config.deferredUpload) {
        this.uploadFile(fileObj);
        this.files.push(fileObj);
      } else {
        this.files.push(fileObj);
        this.emitValue();
      }
    })
  }

  public async uploadFile(file: UploadFile): Promise<ObjectItem> {
    file.status = null;
    file.progress = 1;
    const [request, error] = await awaitHandler(this.getRequestParams(file));

    if (error) {
      this.onFileError(file);
      return Promise.reject(error);
    }

    return new Promise((resolve, reject) => {
      const errorMessage = this.translation.instant('SHARED.uploadFileError');
      let hasRealProgress = false;
      let progressSimulator: any = null;

      // Estimate upload time based on file size
      // Assume ~5 MB/s average upload speed (adjust based on your typical network)
      const fileSizeMB = file.data.size / (1024 * 1024);
      const estimatedSeconds = Math.max(fileSizeMB / 5, 2); // Minimum 2 seconds
      const targetProgress = 80; // Stop simulated progress at 80%
      const incrementInterval = (estimatedSeconds * 1000) / (targetProgress - 1); // Time per 1% increment

      // Start simulated progress after a short delay if no real progress events arrive
      const simulatorTimeout = setTimeout(() => {
        if (!hasRealProgress && file.progress < targetProgress) {
          progressSimulator = setInterval(() => {
            if (file.progress < targetProgress) {
              // Slow down progress after 60% to avoid waiting at 80%
              const progressRate = file.progress > 60 ? 0.5 : 1;
              file.progress = Math.min(file.progress + progressRate, targetProgress);
            } else {
              clearInterval(progressSimulator);
            }
          }, incrementInterval);
        }
      }, 500);

      this.http.request(request).pipe(
        tap((event: HttpEvent<any>) => {
          this.logger.debug('Upload event', event);
          if (event.type === HttpEventType.UploadProgress && event.total) {
            hasRealProgress = true;
            // Clear simulated progress if we get real events
            if (progressSimulator) {
              clearInterval(progressSimulator);
              progressSimulator = null;
            }
            clearTimeout(simulatorTimeout);
            file.progress = Math.round((80 * event.loaded) / event.total);
          }
        }),
        filter(event => event.type === HttpEventType.Response),
        switchMap(() => this.requestUploadObject(file)),
        catchError((e) => {
          clearTimeout(simulatorTimeout);
          if (progressSimulator) {
            clearInterval(progressSimulator);
          }
          reject(errorMessage);
          return of(e);
        })
      ).subscribe((response: ObjectItem) => {
        clearTimeout(simulatorTimeout);
        if (progressSimulator) {
          clearInterval(progressSimulator);
        }
        if (response?.objectID) {
          resolve(response);
          this.onFileSuccess(file, response);
        } else {
          reject(errorMessage);
          this.onFileError(file);
        }
      });
    });
  }

  public async upload(metadata?: FormFileUploaderMetadata): Promise<ObjectItem[]> {
    if (this.config.deferredUpload) {
      this.config.metadata = assign({}, this.config.metadata, metadata);
      const files = reject(this.files, { status: UploadFileStatus.Done });
      let [objects, error] = await awaitHandler(Promise.all(map(files, (file) => this.uploadFile(file))));

      if (!error) {
        await awaitHandler(Promise.all(map(this.markedForRemovalIds, (id) => this.objectsService.removeObject(id))));
      }

      if (objects?.length) {
        objects = _filter(objects);
        this.tempUploadedObjects = objects;
      }

      if (files.length === 0 && this.tempUploadedObjects?.length) {
        return Promise.resolve(this.tempUploadedObjects);
      } else {
        return error ? Promise.reject(error) : Promise.resolve(objects);
      }
    } else {
      return Promise.resolve([]);
    }
  }

  public open(file: UploadFile) {
    if (file?.object?.objectID) {
      const revision = this.config?.metadata?.revision;
      if (revision) {
        // When viewing a specific revision, build the URL with the revision parameter
        const objectUrl = this.commsService.objectURI(file.object.objectID, false, false, true, revision);
        const mediaType = file.object.mediaType || 'image/jpeg';
        this.viewerService.showByType(objectUrl, mediaType);
      } else {
        this.viewerService.showByObjectId(file.object.objectID);
      }
    }
  }

  private async getRequestParams(file: UploadFile) {
    const [url] = await awaitHandler(this.getObjectUploadURI(file.object.objectUUID));

    const headers = new HttpHeaders({
      'Content-Type': file.data.type,
    });

    return new HttpRequest('PUT', url, file.data, {
      reportProgress: true,
      headers
    });
  }

  private requestUploadObject(file: UploadFile) {
    const formData = new FormData();
    formData.append('token', this.userDataService.Token);
    formData.append('subscriberID', this.subscriberService.subInfo.subscriberID);
    formData.append('mediaType', file.data.type);
    formData.append('originalName', file.data.name);
    formData.append('uploaded', '1');
    formData.append('objectUUID', file.object.objectUUID);

    each(this.config.metadata, (value, name) => {
      formData.append(name, value);
    });

    return this.http.post(`${this.commsService.backendHost}${this.commsService.uploadPath}?ngsw-bypass`, formData);
  }

  private onFileError(file: UploadFile) {
    file.status = UploadFileStatus.Error;
    file.error = this.translation.instant('SHARED.uploadFileError');
  }

  private onFileSuccess(file: UploadFile, response: ObjectItem) {
    file.object = response;
    file.progress = 100;
    file.status = UploadFileStatus.Done;
    this.emitValue();
  }

  private emitValue() {
    if (this.config.limit === 1) {
      if (this.files?.length && (this.files?.[0]?.status === UploadFileStatus.Done || this.config.deferredUpload)) {
        const file = this.files[0];
        this.formValue = {
          objectID: file?.object?.objectID,
          objectUUID: file?.object?.objectUUID,
          revision: file?.object?.revision
        } as FormFieldUploaderValue;
      } else if (this.files.length === 0) {
        this.formValue = '';
      }
    } else {
      const files = _filter(this.files, { status: UploadFileStatus.Done });

      this.formValue = files?.length ? map(files, (file) => {
        return {
          objectID: file.object.objectID,
          objectUUID: file.object.objectUUID,
          revision: file?.object?.revision
        };
      }) : '';
    }
  }

  private getObjectUploadURI(objectUUID: string) {
    const params = {
      cmd: 'getObjectUploadURI',
      objectUUID
    };
    return this.commsService.sendMessage(params).then((response) => {
      return response?.result?.uploadURI;
    });
  }

  private defineConfig() {
    this.config = assign(<FormFileUploaderConfig>{
      limit: 1,
      accept: 'image/*',
      metadata: {
        type: 'image'
      }
    }, this.config);
  }

  private defineFiles() {
    const ids = _filter(flatten([this.value ?? this.formValue]));
    const revision = this.config?.metadata?.revision;

    each(ids, (id) => {
      let cachedObject = this.objectsService.getCachedObjectById(id);
      
      // If we're viewing a specific revision, get the revision-specific object data
      // to ensure we have the correct mediaType (different revisions may have different media types)
      if (revision) {
        const revisionList = this.objectsService.getRevisionList(id, true);
        const revisionObject = find(revisionList, { revision });
        if (revisionObject) {
          cachedObject = revisionObject;
        }
      }

      const file = {
        icon: this.objectsService.getIconByType(cachedObject?.mediaType || ObjectFileType.Image),
        object: {
          objectID: +id,
          mediaType: cachedObject?.mediaType
        } as ObjectItem,
        status: UploadFileStatus.Done
      } as UploadFile;

      if (includes(cachedObject?.mediaType, ObjectFileType.Image) || !cachedObject?.mediaType) {
        file.preview = this.commsService.objectURI(id, true, undefined, undefined, this.config?.metadata?.revision);
      }

      this.files.push(file);
    });
  }

  private matchesAccept(file: File, accept: string): boolean {
    if (accept === '*') {
      return true;
    }

    const acceptItems = _filter<string>(
      map(split(accept, ','), (item) => toLower(trim(item))),
      Boolean
    );

    const fileType = toLower(file.type);
    const fileName = toLower(file.name);

    return some(acceptItems, (acceptItem) => {
      if (startsWith(acceptItem, '.')) {
        return endsWith(fileName, acceptItem);
      }

      if (endsWith(acceptItem, '/*')) {
        const baseType = split(acceptItem, '/')[0];
        return startsWith(fileType, `${baseType}/`);
      }

      return fileType === acceptItem;
    });
  }
}
